/**
 * Browser-driven locale for hosted authentication portal surfaces.
 * Vietnamese when the browser language is `vi` / `vi-*`; otherwise English.
 * No server locale, URL param, or user language picker.
 */

export type AuthLocale = "vi" | "en";

/** Resolve auth UI locale from browser language tags (BCP 47). */
export function resolveAuthLocale(
  languages: readonly string[] | undefined = browserLanguages(),
): AuthLocale {
  if (!languages?.length) return "en";
  for (const raw of languages) {
    const tag = String(raw ?? "")
      .trim()
      .toLowerCase();
    if (!tag) continue;
    const primary = tag.split(/[-_]/)[0];
    if (primary === "vi") return "vi";
  }
  return "en";
}

function browserLanguages(): readonly string[] {
  if (typeof navigator === "undefined") return ["en"];
  if (navigator.languages?.length) return navigator.languages;
  if (navigator.language) return [navigator.language];
  return ["en"];
}

export type AuthMessages = {
  loadingContext: string;
  linkUnavailableTitle: string;
  linkUnavailableBody: string;
  invalidAuthContext: string;
  mcpTransactionRequired: string;
  authorizationTransactionRequired: string;
  sessionFound: string;
  continueToDashboard: string;
  continueToApp: (appName: string) => string;
  sessionSkipHint: string;
  continue: string;
  platformAccount: string;
  createDashboard: string;
  signInDashboard: string;
  createAccountFor: (appName: string) => string;
  continueToAppHeading: (appName: string) => string;
  platformIdentityHint: string;
  projectIdentityHint: string;
  email: string;
  username: string;
  password: string;
  useEmailInstead: string;
  useUsernameInstead: string;
  pleaseWait: string;
  createAccount: string;
  signIn: string;
  continueWithGoogle: string;
  googleConsentHint: string;
  alreadyHaveAccount: string;
  createAnAccount: string;
  forgotPassword: string;
  recoverAccount: string;
  setNewPassword: string;
  verifyYourEmail: string;
  recoverySent: string;
  recoveryBoundHint: (realm: string) => string;
  sendResetInstructions: string;
  resendVerification: string;
  passwordsDoNotMatch: string;
  passwordUpdated: string;
  emailVerified: string;
  passwordUpdatedBody: string;
  emailVerifiedBody: string;
  continueToSignIn: string;
  oneTimeActionHint: (expiresLocal: string, realm: string) => string;
  newPassword: string;
  confirmPassword: string;
  updatePassword: string;
  verifyEmailCta: string;
  authorizationRequired: string;
  reviewAccessFor: (appName: string) => string;
  consentNeverAuto: string;
  signedInProjectUser: string;
  signedInTo: (projectName: string) => string;
  changeAccount: string;
  requestedPermissions: string;
  destination: string;
  redirectRegistrationHint: string;
  publishedBy: (name: string) => string;
  previousGrant: (count: number) => string;
  cancelling: string;
  cancel: string;
  authorizing: string;
  authorizeAndContinue: string;
  scopeProfile: string;
  scopeEmail: string;
  scopeOffline: string;
  scopeProfileDesc: string;
  scopeEmailDesc: string;
  scopeOfflineDesc: string;
  scopeFallbackDesc: string;
  agentAuthRequired: string;
  connectAgent: (clientName: string) => string;
  agentConsentNeverAuto: string;
  platformOwner: string;
  signedInAsOwner: string;
  projectAgentMayAccess: string;
  oneProjectPerAuth: string;
  allProjectsRequested: string;
  allProjectsConsent: string;
  allProjectsConsentDesc: string;
  loadingProjects: string;
  noActiveProjects: string;
  selectProject: string;
  permissionsRequested: string;
  agentCallback: string;
  mcpResource: string;
  tokensShortLived: string;
  authorizeAgent: string;
  selectProjectError: string;
  selectAllProjectsError: string;
  mcpRead: string;
  mcpWrite: string;
  mcpMigrate: string;
  mcpAdmin: string;
  mcpAllProjects: string;
  mcpReadDesc: string;
  mcpWriteDesc: string;
  mcpMigrateDesc: string;
  mcpAdminDesc: string;
  mcpAllProjectsDesc: string;
  mcpFallbackDesc: string;
  poweredBy: string;
  privacy: string;
  terms: string;
  security: string;
  verified: string;
  hostedAuthentication: string;
};

const en: AuthMessages = {
  loadingContext: "Loading secure sign-in context…",
  linkUnavailableTitle: "This sign-in link is unavailable",
  linkUnavailableBody:
    "Return to the application and begin again. No account changes were made.",
  invalidAuthContext: "Invalid hosted authentication context",
  mcpTransactionRequired:
    "A valid MCP authorization transaction is required.",
  authorizationTransactionRequired:
    "A valid authorization transaction is required.",
  sessionFound: "Session found",
  continueToDashboard: "Continue to TinyBase Dashboard",
  continueToApp: (appName) => `Continue to ${appName}`,
  sessionSkipHint:
    "Credential entry can be skipped for this realm. Application access is still reviewed on the next page.",
  continue: "Continue",
  platformAccount: "Platform account",
  createDashboard: "Create TinyBase Dashboard",
  signInDashboard: "Sign in to TinyBase Dashboard",
  createAccountFor: (appName) => `Create an account for ${appName}`,
  continueToAppHeading: (appName) => `Continue to ${appName}`,
  platformIdentityHint:
    "Use your TinyBase platform owner identity. Project accounts cannot sign in here.",
  projectIdentityHint:
    "This account belongs only to this Project and is separate from your TinyBase Dashboard account.",
  email: "Email",
  username: "Username",
  password: "Password",
  useEmailInstead: "Use email instead",
  useUsernameInstead: "Use username instead",
  pleaseWait: "Please wait…",
  createAccount: "Create account",
  signIn: "Sign in",
  continueWithGoogle: "Continue with Google",
  googleConsentHint: "Google never bypasses the Application consent review.",
  alreadyHaveAccount: "Already have an account?",
  createAnAccount: "Create an account",
  forgotPassword: "Forgot password?",
  recoverAccount: "Recover your account",
  setNewPassword: "Set a new password",
  verifyYourEmail: "Verify your email",
  recoverySent:
    "If this account can use the requested email action, instructions have been sent. Check your inbox and spam folder.",
  recoveryBoundHint: (realm) =>
    `This security action is bound to the current ${realm} realm. We never disclose whether an address is registered.`,
  sendResetInstructions: "Send reset instructions",
  resendVerification: "Resend verification email",
  passwordsDoNotMatch: "Passwords do not match.",
  passwordUpdated: "Password updated",
  emailVerified: "Email verified",
  passwordUpdatedBody:
    "All existing sessions were signed out. Sign in again with your new password.",
  emailVerifiedBody:
    "Your email is verified. You can safely return to the application.",
  continueToSignIn: "Continue to sign in",
  oneTimeActionHint: (expiresLocal, realm) =>
    `This one-time action expires at ${expiresLocal} and works only in this ${realm} realm.`,
  newPassword: "New password",
  confirmPassword: "Confirm password",
  updatePassword: "Update password",
  verifyEmailCta: "Verify email",
  authorizationRequired: "Authorization required",
  reviewAccessFor: (appName) => `Review access for ${appName}`,
  consentNeverAuto:
    "This page always requires your explicit choice. Loading it never grants access.",
  signedInProjectUser: "Signed-in Project user",
  signedInTo: (projectName) => `Signed in to ${projectName}`,
  changeAccount: "Change account",
  requestedPermissions: "Requested permissions",
  destination: "Destination:",
  redirectRegistrationHint:
    "Redirect registration is separate from CORS and does not grant database access.",
  publishedBy: (name) => `Published by ${name}`,
  previousGrant: (count) =>
    `You previously authorized this Application ${count} time${count === 1 ? "" : "s"}. A new explicit choice is still required.`,
  cancelling: "Cancelling…",
  cancel: "Cancel",
  authorizing: "Authorizing…",
  authorizeAndContinue: "Authorize and continue",
  scopeProfile: "Basic profile",
  scopeEmail: "Email address",
  scopeOffline: "Stay signed in",
  scopeProfileDesc: "Read your Project username and basic account profile.",
  scopeEmailDesc: "Read the email address attached to this Project account.",
  scopeOfflineDesc:
    "Receive a rotating refresh token so the Application can keep your session active.",
  scopeFallbackDesc:
    "Access this explicitly enabled Application scope. PostgreSQL RLS still applies.",
  agentAuthRequired: "Agent authorization required",
  connectAgent: (clientName) => `Connect agent: ${clientName}`,
  agentConsentNeverAuto:
    "This page always requires your explicit choice. Loading it never grants access. A new approval is required every time this agent requests access — nothing is remembered.",
  platformOwner: "Platform owner",
  signedInAsOwner:
    "Signed in as the TinyBase platform owner. Project app accounts cannot approve agent access.",
  projectAgentMayAccess: "Project this agent may access",
  oneProjectPerAuth: "Exactly one owned project per authorization. Choose carefully.",
  allProjectsRequested:
    "This request includes access beyond the selected project. Review the extra permission below.",
  allProjectsConsent: "Allow this agent to work across all my active projects",
  allProjectsConsentDesc:
    "This is an elevated choice. Leave it unchecked to cancel instead of granting a narrower token.",
  loadingProjects: "Loading your projects…",
  noActiveProjects:
    "You have no active projects. Create a project in the dashboard first.",
  selectProject: "Select a project…",
  permissionsRequested: "Permissions requested",
  agentCallback: "Agent callback:",
  mcpResource: "MCP resource:",
  tokensShortLived:
    "Tokens are short-lived and bound to this resource. You can revoke access anytime from Account Center or the project Agent access panel.",
  authorizeAgent: "Authorize agent",
  selectProjectError: "Select a project to grant this agent access to.",
  selectAllProjectsError:
    "Explicitly approve access to all your active projects, or cancel this request.",
  mcpRead: "Read project data",
  mcpWrite: "Change project data",
  mcpMigrate: "Change schema and permissions",
  mcpAdmin: "Administer projects and accounts",
  mcpAllProjects: "Access all owned projects",
  mcpReadDesc:
    "Inspect schema, run read queries, and view usage/observability.",
  mcpWriteDesc:
    "Insert, update, and delete rows (filter rules still apply).",
  mcpMigrateDesc:
    "Plan and apply migrations and permission presets (with confirmation).",
  mcpAdminDesc:
    "Manage project settings, keys, users, applications, and agent grants.",
  mcpAllProjectsDesc:
    "Allow this agent to target any active project owned by you, not only the selected project.",
  mcpFallbackDesc: "MCP agent capability for this project only.",
  poweredBy: "Powered by TinyBase",
  privacy: "Privacy",
  terms: "Terms",
  security: "Security",
  verified: "Verified",
  hostedAuthentication: "Hosted authentication",
};

const vi: AuthMessages = {
  loadingContext: "Đang tải ngữ cảnh đăng nhập an toàn…",
  linkUnavailableTitle: "Liên kết đăng nhập không khả dụng",
  linkUnavailableBody:
    "Hãy quay lại ứng dụng và bắt đầu lại. Không có thay đổi nào trên tài khoản.",
  invalidAuthContext: "Ngữ cảnh xác thực được lưu trữ không hợp lệ",
  mcpTransactionRequired: "Cần giao dịch ủy quyền MCP hợp lệ.",
  authorizationTransactionRequired: "Cần giao dịch ủy quyền hợp lệ.",
  sessionFound: "Đã có phiên đăng nhập",
  continueToDashboard: "Tiếp tục tới Bảng điều khiển TinyBase",
  continueToApp: (appName) => `Tiếp tục tới ${appName}`,
  sessionSkipHint:
    "Bạn có thể bỏ qua bước nhập mật khẩu cho phạm vi này. Quyền truy cập ứng dụng vẫn được xem lại ở trang tiếp theo.",
  continue: "Tiếp tục",
  platformAccount: "Tài khoản nền tảng",
  createDashboard: "Tạo tài khoản Bảng điều khiển TinyBase",
  signInDashboard: "Đăng nhập Bảng điều khiển TinyBase",
  createAccountFor: (appName) => `Tạo tài khoản cho ${appName}`,
  continueToAppHeading: (appName) => `Tiếp tục tới ${appName}`,
  platformIdentityHint:
    "Dùng danh tính chủ sở hữu nền tảng TinyBase. Tài khoản Project không đăng nhập được tại đây.",
  projectIdentityHint:
    "Tài khoản này chỉ thuộc Project này và tách biệt với tài khoản Bảng điều khiển TinyBase.",
  email: "Email",
  username: "Tên người dùng",
  password: "Mật khẩu",
  useEmailInstead: "Dùng email",
  useUsernameInstead: "Dùng tên người dùng",
  pleaseWait: "Vui lòng đợi…",
  createAccount: "Tạo tài khoản",
  signIn: "Đăng nhập",
  continueWithGoogle: "Tiếp tục với Google",
  googleConsentHint:
    "Google không bỏ qua bước xem xét đồng ý của Ứng dụng.",
  alreadyHaveAccount: "Đã có tài khoản?",
  createAnAccount: "Tạo tài khoản",
  forgotPassword: "Quên mật khẩu?",
  recoverAccount: "Khôi phục tài khoản",
  setNewPassword: "Đặt mật khẩu mới",
  verifyYourEmail: "Xác minh email",
  recoverySent:
    "Nếu tài khoản có thể dùng thao tác email này, hướng dẫn đã được gửi. Kiểm tra hộp thư và thư rác.",
  recoveryBoundHint: (realm) =>
    `Thao tác bảo mật này gắn với phạm vi ${realm} hiện tại. Chúng tôi không tiết lộ địa chỉ có được đăng ký hay không.`,
  sendResetInstructions: "Gửi hướng dẫn đặt lại",
  resendVerification: "Gửi lại email xác minh",
  passwordsDoNotMatch: "Mật khẩu không khớp.",
  passwordUpdated: "Đã cập nhật mật khẩu",
  emailVerified: "Đã xác minh email",
  passwordUpdatedBody:
    "Tất cả phiên hiện tại đã đăng xuất. Hãy đăng nhập lại bằng mật khẩu mới.",
  emailVerifiedBody:
    "Email của bạn đã được xác minh. Bạn có thể quay lại ứng dụng an toàn.",
  continueToSignIn: "Tiếp tục đăng nhập",
  oneTimeActionHint: (expiresLocal, realm) =>
    `Thao tác một lần này hết hạn lúc ${expiresLocal} và chỉ có hiệu lực trong phạm vi ${realm}.`,
  newPassword: "Mật khẩu mới",
  confirmPassword: "Xác nhận mật khẩu",
  updatePassword: "Cập nhật mật khẩu",
  verifyEmailCta: "Xác minh email",
  authorizationRequired: "Cần ủy quyền",
  reviewAccessFor: (appName) => `Xem lại quyền truy cập cho ${appName}`,
  consentNeverAuto:
    "Trang này luôn yêu cầu lựa chọn rõ ràng của bạn. Chỉ mở trang không cấp quyền.",
  signedInProjectUser: "Người dùng Project đã đăng nhập",
  signedInTo: (projectName) => `Đã đăng nhập vào ${projectName}`,
  changeAccount: "Đổi tài khoản",
  requestedPermissions: "Quyền được yêu cầu",
  destination: "Đích:",
  redirectRegistrationHint:
    "Đăng ký redirect tách biệt CORS và không cấp quyền cơ sở dữ liệu.",
  publishedBy: (name) => `Phát hành bởi ${name}`,
  previousGrant: (count) =>
    `Bạn đã ủy quyền Ứng dụng này ${count} lần trước đây. Vẫn cần một lựa chọn rõ ràng mới.`,
  cancelling: "Đang hủy…",
  cancel: "Hủy",
  authorizing: "Đang ủy quyền…",
  authorizeAndContinue: "Ủy quyền và tiếp tục",
  scopeProfile: "Hồ sơ cơ bản",
  scopeEmail: "Địa chỉ email",
  scopeOffline: "Duy trì đăng nhập",
  scopeProfileDesc:
    "Đọc tên người dùng Project và hồ sơ tài khoản cơ bản của bạn.",
  scopeEmailDesc: "Đọc địa chỉ email gắn với tài khoản Project này.",
  scopeOfflineDesc:
    "Nhận refresh token luân phiên để Ứng dụng giữ phiên của bạn.",
  scopeFallbackDesc:
    "Truy cập scope Ứng dụng được bật tường minh. PostgreSQL RLS vẫn áp dụng.",
  agentAuthRequired: "Cần ủy quyền cho agent",
  connectAgent: (clientName) => `Kết nối agent: ${clientName}`,
  agentConsentNeverAuto:
    "Trang này luôn yêu cầu lựa chọn rõ ràng của bạn. Chỉ mở trang không cấp quyền. Mỗi lần agent yêu cầu đều cần phê duyệt mới — không lưu lại.",
  platformOwner: "Chủ sở hữu nền tảng",
  signedInAsOwner:
    "Đã đăng nhập với tư cách chủ sở hữu nền tảng TinyBase. Tài khoản app Project không phê duyệt quyền agent được.",
  projectAgentMayAccess: "Project agent được phép truy cập",
  oneProjectPerAuth:
    "Chỉ một project sở hữu cho mỗi lần ủy quyền. Hãy chọn cẩn thận.",
  allProjectsRequested:
    "Yêu cầu này bao gồm quyền vượt ra ngoài project đã chọn. Hãy xem kỹ quyền bổ sung bên dưới.",
  allProjectsConsent: "Cho phép agent này làm việc trên mọi project đang hoạt động của tôi",
  allProjectsConsentDesc:
    "Đây là lựa chọn quyền cao hơn. Bỏ chọn sẽ hủy yêu cầu thay vì cấp token hẹp hơn.",
  loadingProjects: "Đang tải project của bạn…",
  noActiveProjects:
    "Bạn chưa có project đang hoạt động. Hãy tạo project trong bảng điều khiển trước.",
  selectProject: "Chọn một project…",
  permissionsRequested: "Quyền được yêu cầu",
  agentCallback: "Callback của agent:",
  mcpResource: "Tài nguyên MCP:",
  tokensShortLived:
    "Token có thời hạn ngắn và gắn với tài nguyên này. Bạn có thể thu hồi bất cứ lúc nào từ Trung tâm tài khoản hoặc bảng Agent access của project.",
  authorizeAgent: "Ủy quyền agent",
  selectProjectError: "Chọn một project để cấp quyền cho agent này.",
  selectAllProjectsError:
    "Hãy xác nhận quyền truy cập mọi project đang hoạt động, hoặc hủy yêu cầu này.",
  mcpRead: "Đọc dữ liệu project",
  mcpWrite: "Thay đổi dữ liệu project",
  mcpMigrate: "Thay đổi schema và quyền",
  mcpAdmin: "Quản trị project và tài khoản",
  mcpAllProjects: "Truy cập mọi project sở hữu",
  mcpReadDesc:
    "Xem schema, chạy truy vấn đọc và xem usage/quan sát.",
  mcpWriteDesc:
    "Thêm, cập nhật và xóa hàng (quy tắc lọc vẫn áp dụng).",
  mcpMigrateDesc:
    "Lập kế hoạch và áp dụng migration cùng preset quyền (có xác nhận).",
  mcpAdminDesc:
    "Quản lý cài đặt project, key, người dùng, ứng dụng và quyền agent.",
  mcpAllProjectsDesc:
    "Cho phép agent nhắm tới mọi project đang hoạt động do bạn sở hữu, không chỉ project đã chọn.",
  mcpFallbackDesc: "Khả năng agent MCP chỉ cho project này.",
  poweredBy: "Được cung cấp bởi TinyBase",
  privacy: "Quyền riêng tư",
  terms: "Điều khoản",
  security: "Bảo mật",
  verified: "Đã xác minh",
  hostedAuthentication: "Xác thực được lưu trữ",
};

/** Message table for the resolved (or forced) locale. */
export function getAuthMessages(locale?: AuthLocale): AuthMessages {
  const resolved = locale ?? resolveAuthLocale();
  return resolved === "vi" ? vi : en;
}

export function scopeLabelMessage(scope: string, t: AuthMessages): string {
  switch (scope) {
    case "profile":
      return t.scopeProfile;
    case "email":
      return t.scopeEmail;
    case "offline_access":
      return t.scopeOffline;
    default:
      return scope;
  }
}

export function scopeExplanationMessage(
  scope: string,
  t: AuthMessages,
): string {
  switch (scope) {
    case "profile":
      return t.scopeProfileDesc;
    case "email":
      return t.scopeEmailDesc;
    case "offline_access":
      return t.scopeOfflineDesc;
    default:
      return t.scopeFallbackDesc;
  }
}

export function mcpScopeLabelMessage(scope: string, t: AuthMessages): string {
  switch (scope) {
    case "mcp:read":
      return t.mcpRead;
    case "mcp:write":
      return t.mcpWrite;
    case "mcp:migrate":
      return t.mcpMigrate;
    case "mcp:admin":
      return t.mcpAdmin;
    case "mcp:all_projects":
      return t.mcpAllProjects;
    default:
      return scope;
  }
}

export function mcpScopeExplanationMessage(
  scope: string,
  t: AuthMessages,
): string {
  switch (scope) {
    case "mcp:read":
      return t.mcpReadDesc;
    case "mcp:write":
      return t.mcpWriteDesc;
    case "mcp:migrate":
      return t.mcpMigrateDesc;
    case "mcp:admin":
      return t.mcpAdminDesc;
    case "mcp:all_projects":
      return t.mcpAllProjectsDesc;
    default:
      return t.mcpFallbackDesc;
  }
}
